import React, { useState, useEffect, useCallback } from "react";
import { format, subDays } from "date-fns";

import {
  makeStyles,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const ACTION_COLORS = {
  cerrar: "#4caf50",
  abrir: "#2196f3",
  eliminar: "#f44336",
  actualizar: "#ff9800",
  transferir: "#9c27b0",
  crear: "#009688",
};

const AuditLogs = () => {
  const classes = useStyles();
  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 7), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(new Date(), "yyyy-MM-dd"));

  const fetchLogs = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-logs", {
        params: { pageNumber: pageNum + 1, dateFrom, dateTo },
      });
      setLogs(data.logs);
      setCount(data.count);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchLogs(0);
  }, []);

  const handleSearch = () => {
    setPage(0);
    fetchLogs(0);
  };

  const handlePageChange = (e, newPage) => {
    setPage(newPage);
    fetchLogs(newPage);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Auditoria</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            label="Desde"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
            style={{ width: 160 }}
          />
          <TextField
            label="Hasta"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
            style={{ width: 160 }}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSearch}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            disabled={loading}
          >
            Buscar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Accion</TableCell>
                <TableCell>Entidad</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Detalles</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    {format(new Date(log.createdAt), "dd/MM/yyyy HH:mm")}
                  </TableCell>
                  <TableCell>{log.userName || `ID: ${log.userId}`}</TableCell>
                  <TableCell>
                    <Chip
                      label={log.action}
                      size="small"
                      style={{
                        backgroundColor: ACTION_COLORS[log.action] || "#757575",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    />
                  </TableCell>
                  <TableCell>{log.entity}</TableCell>
                  <TableCell>{log.entityId || "-"}</TableCell>
                  <TableCell>{log.details || "-"}</TableCell>
                </TableRow>
              ))}
              {!loading && logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No hay registros de auditoria
                  </TableCell>
                </TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={50}
          rowsPerPageOptions={[50]}
          onPageChange={handlePageChange}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>
    </MainContainer>
  );
};

export default AuditLogs;
